import { YOUR_BATCH_ARRAY as yourBatchArray } from "../../../../constants/visualizationOptions";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  TextField,
  IconButton,
  Grid,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Label from "../../../../components/Label/Label";
import "./BatchLIstPreview.scss";

const BatchPreview = ({
  showPreview,
  setShowPreview,
  previewData,
  setPreviewData,
  onClickApply,
  disableAllFields,
  batchLoader,
}) => {
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState(previewData);

  // New: local state for selection in child
  const [localData, setLocalData] = useState([]);

  // Initialize localData when previewData changes
  useEffect(() => {
    setLocalData(previewData.map((batch) => ({ ...batch })));
  }, [previewData]);

  // Filter based on search
  useEffect(() => {
    const filtered = localData.filter((batch) =>
      batch.batch_id.toLowerCase().includes(search.toLowerCase()),
    );
    setFilteredData(filtered);
  }, [search, localData]);

  // Handle select all / clear all
  const handleSelectAll = (checked) => {
    setLocalData((prev) =>
      prev.map((batch) =>
        filteredData.some((f) => f.batch_id === batch.batch_id)
          ? { ...batch, isSelected: checked }
          : batch,
      ),
    );
  };

  // Toggle individual batch selection
  const toggleSelect = (batch_id) => {
    setLocalData((prev) =>
      prev.map((batch) =>
        batch.batch_id === batch_id
          ? { ...batch, isSelected: !batch.isSelected }
          : batch,
      ),
    );
  };

  // Check if all filtered items are selected
  const isAllSelected =
    filteredData.length > 0 && filteredData.every((batch) => batch.isSelected);

  // Total selected in localData
  const totalSelected = localData.filter((b) => b.isSelected).length;

  return (
    <Box className="batch-preview-panel">
      <Card elevation={0}>
        <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box className="header">
            <Label
              text={`Batch Preview ${totalSelected}/${localData.length}`}
              fontWeight="500"
            />
            <IconButton
              size="small"
              onClick={() => setShowPreview(false)}
              aria-label="close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Search */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search Batch ID"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />

          {/* Table */}
          {batchLoader ? (
            <Box className="loading-container">
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ width: "100%", overflow: "auto" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell
                      padding="checkbox"
                      sx={{
                        padding: "4px 6px",
                        fontWeight: "bold",
                        backgroundColor: "#e0e0e0",
                      }}
                    >
                      <Checkbox
                        checked={isAllSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        size="small"
                        sx={{
                          color: "#fff",
                          "&.Mui-checked .MuiSvgIcon-root": {
                            backgroundColor: "#ffffffff",
                            borderRadius: "3px",
                          },
                          "& .MuiSvgIcon-root": {
                            borderRadius: "3px",
                          },
                        }}
                        disabled={disableAllFields}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "4px 6px",
                        fontWeight: "bold",
                        backgroundColor: "#e0e0e0",
                      }}
                    >
                      Batch ID
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "4px 6px",
                        fontWeight: "bold",
                        backgroundColor: "#e0e0e0",
                      }}
                    >
                      Date
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* Table Body */}
                <TableBody>
                  {filteredData.map((batch, index) => (
                    <TableRow key={index} hover>
                      <TableCell padding="checkbox" sx={{ padding: "8px 2px" }}>
                        <Checkbox
                          checked={batch.isSelected}
                          onChange={() => toggleSelect(batch.batch_id)}
                          size="small"
                          disabled={disableAllFields}
                        />
                      </TableCell>
                      <TableCell sx={{ padding: "8px 2px" }}>
                        <Label text={batch.batch_id} />
                      </TableCell>
                      <TableCell sx={{ padding: "8px 2px" }}>
                        <Label text={batch.batch_date} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Footer */}
          <Box className="footer-btns">
            <Box className="left-btns">
              <Button
                size="small"
                variant="text"
                onClick={() => handleSelectAll(true)}
                disabled={disableAllFields}
              >
                <Label
                  text="Select All"
                  fontSize="12px"
                  fontWeight="500"
                  color="inherit"
                />
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => totalSelected >= 0 && handleSelectAll(false)}
                disabled={disableAllFields}
              >
                <Label
                  text="Clear All"
                  fontSize="12px"
                  fontWeight="500"
                  color={totalSelected === 0 ? "gray" : "inherit"}
                />
              </Button>
            </Box>
            <Button
              disabled={totalSelected === 0 || disableAllFields}
              size="small"
              variant="contained"
              onClick={() =>
                onClickApply(
                  localData.filter((b) => b.isSelected).map((b) => b.batch_id),
                  true,
                )
              }
            >
              <Label
                text="Apply"
                color="inherit"
                fontSize="12px"
                fontWeight="500"
              />
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BatchPreview;